import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Bot, X, Sparkles, Mail } from "lucide-react";
import { dict } from "../i18n";
import { streamChat } from "../lib/api";
import InquiryForm from "./InquiryForm";
import MessageList from "./ai/MessageList";
import InputBar from "./ai/InputBar";
import useVoiceInput from "../hooks/useVoiceInput";

/**
 * AI Assistant panel — floating chat window that streams responses from the
 * Claude-backed /api/chat/stream endpoint, and shows system cards when the
 * assistant dispatches an inquiry email.
 *
 * Composition:
 *   - MessageList     — scrollable chat bubbles + inquiry_sent / _failed cards
 *   - InputBar        — text input + voice mic + send button
 *   - useVoiceInput   — SpeechRecognition wrapper
 *
 * External components can open the panel via `window.dispatchEvent(new
 * CustomEvent("open-ai-assistant"))` — see NotFound.jsx.
 */
export default function AIAssistant({ lang }) {
  const t = dict[lang].ai;
  const [open, setOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState(() => [{ role: "assistant", content: t.greeting }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [cookieBannerVisible, setCookieBannerVisible] = useState(false);
  const scrollRef = useRef(null);

  const voice = useVoiceInput({ lang, onTranscript: setInput });

  // Allow other components (e.g. NotFound page) to open the panel via a
  // custom event, so we don't need to prop-drill setter functions through
  // the router tree.
  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("open-ai-assistant", openHandler);
    const cookieHandler = (e) => setCookieBannerVisible(Boolean(e.detail?.visible));
    window.addEventListener("cookie-banner", cookieHandler);
    return () => {
      window.removeEventListener("open-ai-assistant", openHandler);
      window.removeEventListener("cookie-banner", cookieHandler);
    };
  }, []);

  useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const appendMessage = (msg) => setMessages((m) => [...m, msg]);

  const appendDelta = (chunk) =>
    setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = {
        role: "assistant",
        content: copy[copy.length - 1].content + chunk,
      };
      return copy;
    });

  const sendMsg = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [
      ...m,
      { role: "user", content: text },
      { role: "assistant", content: "" },
    ]);
    setBusy(true);
    try {
      await streamChat({
        session_id: sessionId,
        message: text,
        language: lang,
        onDelta: appendDelta,
        onDone: (d) => { if (d.session_id) setSessionId(d.session_id); },
        onInquirySent: (data) =>
          appendMessage({
            role: "system",
            kind: "inquiry_sent",
            inquiry_type: data.inquiry_type,
            recipient: data.recipient,
          }),
        onInquiryFailed: (data) =>
          appendMessage({
            role: "system",
            kind: "inquiry_failed",
            reason: data.reason || "unknown",
            fields: data.fields || [],
          }),
        onError: (err) => {
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: `⚠️ ${err.message}` };
            return copy;
          });
        },
      });
    } finally {
      setBusy(false);
    }
  };

  const newChat = () => {
    setSessionId(null);
    setMessages([{ role: "assistant", content: t.greeting }]);
  };

  const toggleVoice = () => {
    if (voice.listening) voice.stop();
    else voice.start(input);
  };

  return (
    <>
      {/* FAB — hidden while the panel is open so it can't overlap panel controls,
          and while the cookie-consent card occupies the same bottom-right corner. */}
      {!open && !cookieBannerVisible && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ai-fab"
          className="fixed bottom-24 right-5 z-[10000] fab-aide text-white rounded-full px-5 py-3.5 flex items-center gap-2.5 animate-pulse-ring hover:scale-105 transition-transform"
          aria-label="Open AI assistant"
        >
          <div className="relative">
            <Bot className="h-5 w-5" />
            <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-teal-200 animate-pulse" />
          </div>
          <div className="text-left leading-tight">
            <div className="text-[14px] font-medium">{t.fab}</div>
          </div>
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-[65] bg-surface-ink/30 backdrop-blur-sm animate-fade-in"
          onClick={() => setOpen(false)}
          data-testid="ai-backdrop"
        />
      )}

      {open && (
        <aside
          className="fixed right-0 top-0 z-[70] h-full w-full sm:w-[420px] glass-panel border-l border-surface-outline/60 shadow-glass flex flex-col animate-slide-in-right"
          data-testid="ai-panel"
        >
          <header className="flex items-center justify-between px-5 py-4 border-b border-surface-outline/40 bg-gradient-to-r from-sapphire-700 to-[#005a87] text-white">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-white/15 flex items-center justify-center backdrop-blur">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="leading-tight">
                <div className="text-[15px] font-semibold">{t.title}</div>
                <div className="text-[12px] opacity-80">{t.sub}</div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="h-9 w-9 rounded-lg hover:bg-white/15 flex items-center justify-center"
              data-testid="ai-close-btn"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </header>

          {showForm ? (
            <div className="flex-1 overflow-y-auto p-5" data-testid="ai-form-view">
              <button
                onClick={() => setShowForm(false)}
                className="text-[13px] text-sapphire-700 mb-4 hover:underline"
                data-testid="ai-back-to-chat"
              >
                ← {t.title}
              </button>
              <InquiryForm lang={lang} variant="compact" />
            </div>
          ) : (
            <>
              <MessageList
                messages={messages}
                busy={busy}
                lang={lang}
                thinkingLabel={t.thinking}
                scrollRef={scrollRef}
              />

              <div className="px-4 pb-2 flex flex-wrap gap-2" data-testid="ai-quick-actions">
                <button
                  onClick={() => setShowForm(true)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-teal-200/60 px-3 py-1.5 text-[12px] font-medium text-teal-500 hover:bg-teal-200"
                  data-testid="ai-open-form-btn"
                >
                  <Mail className="h-3.5 w-3.5" /> {t.bookCta}
                </button>
                {messages.length > 2 && (
                  <button
                    onClick={newChat}
                    className="inline-flex items-center rounded-full bg-sapphire-50 px-3 py-1.5 text-[12px] font-medium text-sapphire-700 hover:bg-sapphire-100"
                    data-testid="ai-new-chat-btn"
                  >
                    {t.newChat}
                  </button>
                )}
              </div>

              <InputBar
                lang={lang}
                value={input}
                onChange={setInput}
                onSubmit={sendMsg}
                busy={busy}
                placeholder={t.placeholder}
                sendLabel={t.send}
                voice={{ supported: voice.supported, listening: voice.listening, toggle: toggleVoice }}
              />
            </>
          )}
        </aside>
      )}
    </>
  );
}
